/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    useMultiStyleConfig,
    Box,
    Flex,
    IconButton,
    Text,
    Badge,
    Link as ChakraLink,
} from '@chakra-ui/react'

import Link from '@salesforce/retail-react-app/app/components/link'
// import Search from '@salesforce/retail-react-app/app/components/search'

import {useWishList} from '../../hooks/use-wish-list'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {FavouriteIcon, BurgerIcon, BrandLogo, SupportIcon} from '../icons'
import {useGetMainMenu} from '../../custom-api/hooks/use-get-main-menu'
import {useGetFooterByContentType} from '../../custom-api/hooks/use-get-footer'
import SearchModal from '../search/partials/search-modal'
import {useWishlistUtilities} from '../../context/wishlist'
import HeaderAccount from './header-account'
import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import LoadingSpinner from '../loading-spinner'
import {BRAND_KEY, BRAND_LABEL} from '../../brand'
import {Link as RouterLink} from 'react-router-dom'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import HeaderCartButton from '../header-cart-button'

/**
 * Search bar for the header.
 *
 * The search bar is a simple input field with a search icon.
 * It can be used to search for products or navigate to a
 * specific page.
 *
 * @param props {object} the component props
 * @returns {Element} the search bar element
 */
// const SearchBar = (props) => {
//     const styles = useMultiStyleConfig('Header')
//     const intl = useIntl()
//     const placeholder = intl.formatMessage({
//         id: 'header.field.placeholder.search_for_products',
//         defaultMessage: 'Search for products...',
//     })
//     return (
//         <Box {...styles.searchContainer}>
//             <Search
//                 aria-label={placeholder}
//                 placeholder={placeholder}
//                 {...styles.search}
//                 {...props}
//             />
//         </Box>
//     )
// }
/**
 * The header is the main source for accessing
 * navigation, search, basket, and other
 * important information and actions. It persists
 * on the top of your application and will
 * respond to changes in device size.
 *
 * To customize the styles, update the themes
 * in theme/components/project/header.js
 * @param  props
 * @param   {func} props.onMenuClick click event handler for menu button
 * @param   {func} props.onLogoClick click event handler for menu button
 * @param   {object} props.searchInputRef reference of the search input
 * @param   {func} props.onMyAccountClick click event handler for my account button
 * @param   {func} props.onMyCartClick click event handler for my cart button
 * @return  {React.ReactElement} - Header component
 */
const Header = ({
    children,
    onMenuClick = noop,
    onLogoClick = noop,
    onMyCartClick = noop,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    onMyAccountClick = noop,
    onWishlistClick = noop,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isOpen,
    ...props
}) => {
    const intl = useIntl()

    const {data} = useGetMainMenu(BRAND_KEY === 'kenwood' ? 'kenwood_' : '')
    const [showLoading, setShowLoading] = useState(false)
    // tracking if users enter the popover Content,
    // so we can decide whether to close the menu when users leave account icons
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')
    const hideAccountPref = useBooleanCustomPreference('hideAccount')
    const hideAccount = hideAccountPref !== null ? hideAccountPref : false
    const styles = useMultiStyleConfig('Header')
    const {data: wishListData} = useWishList()
    const wishListNumber = wishListData?.customerProductListItems?.length
    const headerLinks = useMemo(() => data?.headerlinks?.[0]?.links ?? [], [data])
    const {icon: wishlistIconRef} = useWishlistUtilities()
    const {locale} = useMultiSite()
    const {data: footerData} = useGetFooterByContentType()

    const accessibilityStatementLink = useMemo(
        () =>
            footerData?.footer_content?.footer_reference_components
                ?.find((item) => item?._content_type_uid === 'footerbottom')
                ?.footer_bottom_group?.copyright_group?.copyright_links?.find((item) =>
                    /accessibility/i.test(item.link?.href),
                ),
        [footerData],
    )

    return (
        <Box {...styles.headerBar} id="main-header">
            <Box {...styles.container} sx={styles.containerSpacing} layerStyle="page" {...props}>
                <Box {...styles.content} sx={styles.desktopContent}>
                    <Flex {...styles.iconsFlex} sx={styles.hideOnDesktop}>
                        <IconButton
                            aria-label={intl.formatMessage({
                                id: 'header.button.assistive_msg.menu',
                                defaultMessage: 'Menu',
                            })}
                            title={intl.formatMessage({
                                id: 'header.button.assistive_msg.menu.open_dialog',
                                defaultMessage: 'Opens a dialog',
                            })}
                            icon={<BurgerIcon {...styles.headerIcon} />}
                            variant="unstyled"
                            onClick={onMenuClick}
                            {...styles.buttonIcons}
                        />
                        <SearchModal />
                    </Flex>
                    {showLoading && <LoadingSpinner wrapperStyles={{height: '100vh'}} />}
                    <Box
                        {...styles.bodyContainer}
                        sx={{...styles.bodyContainerGap, ...styles.desktopLogo}}
                    >
                        <IconButton
                            aria-label={`${BRAND_LABEL} ${intl.formatMessage({
                                id: 'header.button.assistive_msg.logo',
                                defaultMessage: 'Logo',
                            })}`}
                            icon={<BrandLogo sx={styles.logo} tabIndex={-1} />}
                            variant="unstyled"
                            role="link"
                            borderRadius="medium"
                            tabIndex={0}
                            height="100% !important"
                            onClick={onLogoClick}
                        />
                        {accessibilityStatementLink?.link && (
                            <ChakraLink
                                as={RouterLink}
                                to={`/${locale.alias}${accessibilityStatementLink?.link.href}`}
                                {...styles.srOnly}
                            >
                                {accessibilityStatementLink?.link.title}
                            </ChakraLink>
                        )}
                    </Box>
                    <Box data-testid="header-first-level-links" sx={styles.hideOnMobile}>
                        {children}
                    </Box>
                    <Flex {...styles.linksAndIcons} sx={styles.desktopActions}>
                        {BRAND_KEY !== 'kenwood' &&
                            headerLinks?.map((headerLink) => (
                                <Text
                                    {...styles.links}
                                    sx={styles.showOnXl}
                                    key={headerLink._metadata.uid}
                                >
                                    {headerLink?.is_external_url ? (
                                        <ChakraLink
                                            href={headerLink?.link?.href}
                                            isExternal
                                            rel="noopener noreferrer"
                                        >
                                            {headerLink?.link?.title}
                                        </ChakraLink>
                                    ) : (
                                        <Link to={headerLink.link.href}>
                                            {headerLink?.link?.title}
                                        </Link>
                                    )}
                                </Text>
                            ))}
                        <Flex {...styles.iconsFlex} sx={styles.iconsFlexGap}>
                            <Box sx={styles.hideOnMobile}>
                                <SearchModal />
                            </Box>
                            {BRAND_KEY === 'kenwood' && headerLinks?.[0] && (
                                <ChakraLink
                                    as={RouterLink}
                                    to={headerLinks?.[0]?.link?.href || '/'}
                                    key={headerLinks?.[0]?._metadata?.uid}
                                >
                                    <SupportIcon {...styles.supportIcon} />
                                </ChakraLink>
                            )}
                            {!hideAccount && (
                                <IconButton
                                    aria-label={intl.formatMessage({
                                        defaultMessage: 'Wishlist',
                                        id: 'header.button.assistive_msg.wishlist',
                                    })}
                                    icon={
                                        <>
                                            <FavouriteIcon sx={styles.headerIcon} />
                                            {wishListNumber > 0 && (
                                                <Badge
                                                    {...styles.wishlistBadge}
                                                    data-testid="cart-item-count"
                                                    variant="notification"
                                                >
                                                    {wishListNumber}
                                                </Badge>
                                            )}
                                        </>
                                    }
                                    variant="ghost"
                                    onClick={onWishlistClick}
                                    {...styles.buttonIcons}
                                    sx={styles.hideOnMobile}
                                />
                            )}
                            {!hideAccount && (
                                <HeaderAccount
                                    setShowLoading={setShowLoading}
                                    headerLinks={headerLinks}
                                />
                            )}
                            {!notShoppableCountry && (
                                <Box position="relative">
                                    <HeaderCartButton onMyCartClick={onMyCartClick} />
                                    <Box
                                        ref={wishlistIconRef}
                                        top="190%"
                                        right="0%"
                                        position="absolute"
                                    ></Box>
                                </Box>
                            )}
                        </Flex>
                    </Flex>
                </Box>
            </Box>
        </Box>
    )
}

Header.propTypes = {
    children: PropTypes.node,
    onMenuClick: PropTypes.func,
    onLogoClick: PropTypes.func,
    onMyAccountClick: PropTypes.func,
    onWishlistClick: PropTypes.func,
    onMyCartClick: PropTypes.func,
    onStoreLocatorClick: PropTypes.func,
    searchInputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({current: PropTypes.elementType}),
    ]),
    isOpen: PropTypes.bool,
}

export default Header
